import { create } from "zustand";

export type IslandTaskStatus = "running" | "done" | "failed";

export interface IslandTask {
  id: string;
  /** Present tense, because the island reports work in flight: "Drafting the month". */
  label: string;
  status: IslandTaskStatus;
  /** 0..1 when the work reports steps. Omit for indeterminate work. */
  progress?: number;
  /** A short right-hand readout, e.g. "4 of 12". Mono, never a sentence. */
  detail?: string;
}

interface IslandStore {
  tasks: IslandTask[];
  /** Start or update a task. Same id updates in place, so callers can poll. */
  start: (task: Omit<IslandTask, "status"> & { status?: IslandTaskStatus }) => void;
  update: (id: string, patch: Partial<Omit<IslandTask, "id">>) => void;
  /** Settle a task. It stays visible briefly so the result is readable, then clears. */
  finish: (id: string, status: Exclude<IslandTaskStatus, "running">, label?: string) => void;
  clear: (id: string) => void;
}

/** How long a settled task stays on screen before it clears itself. */
export const ISLAND_SETTLE_MS = 4000;

/**
 * The notification island.
 *
 * Deliberately NOT the toast queue, and deliberately not persisted. The two
 * answer different questions:
 *
 *   Toast  — something HAPPENED. Past tense, 3s, then gone. "Move approved."
 *   Island — something IS HAPPENING. Present tense, long-running, and it stays
 *            until the work settles. "Drafting the month · 4 of 12".
 *
 * Agent work runs for minutes and several jobs overlap, so a stack of toasts is
 * unreadable: they expire before the work finishes and they pile up. One object
 * that morphs holds the whole state instead.
 *
 * Nothing here survives a reload on purpose. A task is a claim about work in
 * flight in THIS session, and restoring one would assert something we cannot
 * know is still true.
 */
export const useIslandStore = create<IslandStore>()((set) => ({
  tasks: [],

  start: ({ status = "running", ...task }) =>
    set((s) => {
      const existing = s.tasks.findIndex((t) => t.id === task.id);
      const next: IslandTask = { ...task, status };
      if (existing === -1) return { tasks: [...s.tasks, next] };
      const tasks = s.tasks.slice();
      tasks[existing] = { ...tasks[existing], ...next };
      return { tasks };
    }),

  update: (id, patch) =>
    set((s) => ({
      tasks: s.tasks.map((t) => (t.id === id ? { ...t, ...patch } : t)),
    })),

  finish: (id, status, label) =>
    set((s) => ({
      tasks: s.tasks.map((t) =>
        t.id === id ? { ...t, status, label: label ?? t.label, progress: undefined } : t,
      ),
    })),

  clear: (id) => set((s) => ({ tasks: s.tasks.filter((t) => t.id !== id) })),
}));
